import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  HeadingLevel,
  AlignmentType,
  BorderStyle,
  ShadingType,
  UnderlineType,
  ExternalHyperlink,
} from 'docx';
import {
  htmlToBlocks,
  sectionHex,
  sectionLabel,
  tint,
  formatExportDate,
  type RichBlock,
  type InlineRun,
  type SermonElement,
  type SermonMeta,
} from './richText';

const BODY_SIZE = 22; // 11pt (half-points)
const INK = '0F1726';
const DIM = '475569';
const MUTED = '94A3B8';

function alignOf(a: RichBlock['align']): (typeof AlignmentType)[keyof typeof AlignmentType] | undefined {
  switch (a) {
    case 'center': return AlignmentType.CENTER;
    case 'right': return AlignmentType.RIGHT;
    case 'justify': return AlignmentType.JUSTIFIED;
    case 'left': return AlignmentType.LEFT;
    default: return undefined;
  }
}

// Convert inline runs (which may contain '\n' from <br>) into docx TextRuns,
// preserving bold/italic/underline/strike/color/highlight and links.
function toTextRuns(
  runs: InlineRun[],
  base?: { bold?: boolean; italic?: boolean; size?: number; color?: string }
): (TextRun | ExternalHyperlink)[] {
  const out: (TextRun | ExternalHyperlink)[] = [];
  for (const r of runs) {
    if (!r.text) continue;
    const segments = r.text.split('\n');
    segments.forEach((seg, i) => {
      const run = new TextRun({
        text: seg,
        break: i > 0 ? 1 : undefined,
        bold: r.bold || base?.bold,
        italics: r.italic || base?.italic,
        strike: r.strike,
        underline: r.underline || r.link ? { type: UnderlineType.SINGLE } : undefined,
        color: r.color || base?.color,
        size: base?.size || BODY_SIZE,
        font: 'Calibri',
        shading: r.highlight
          ? { type: ShadingType.SOLID, color: 'auto', fill: r.highlight }
          : undefined,
      });
      if (r.link) {
        out.push(new ExternalHyperlink({ link: r.link, children: [run] }));
      } else {
        out.push(run);
      }
    });
  }
  return out;
}

function blockToParagraph(b: RichBlock): Paragraph {
  const alignment = alignOf(b.align);

  if (b.kind === 'heading') {
    const heading =
      b.level === 1 ? HeadingLevel.HEADING_2 :
      b.level === 2 ? HeadingLevel.HEADING_3 :
      HeadingLevel.HEADING_4;
    return new Paragraph({
      children: toTextRuns(b.runs, { bold: true }),
      heading,
      alignment,
      spacing: { before: 200, after: 100 },
    });
  }

  if (b.kind === 'blockquote') {
    return new Paragraph({
      children: toTextRuns(b.runs, { italic: true, color: DIM }),
      alignment,
      indent: { left: 720, right: 360 },
      spacing: { before: 160, after: 160, line: 300 },
      border: { left: { style: BorderStyle.SINGLE, size: 18, color: '6366F1', space: 12 } },
    });
  }

  if (b.kind === 'bullet') {
    return new Paragraph({
      children: toTextRuns(b.runs),
      bullet: { level: b.level },
      alignment,
      spacing: { after: 80, line: 300 },
    });
  }

  if (b.kind === 'ordered') {
    // Manual numeric prefix keeps numbering reliable without numbering config.
    const prefix = new TextRun({ text: `${b.ordinal}. `, size: BODY_SIZE, font: 'Calibri' });
    return new Paragraph({
      children: [prefix, ...toTextRuns(b.runs)],
      alignment,
      indent: { left: 360 + b.level * 360, hanging: 260 },
      spacing: { after: 80, line: 300 },
    });
  }

  // paragraph
  return new Paragraph({
    children: toTextRuns(b.runs),
    alignment,
    spacing: { after: 160, line: 300 },
  });
}

function generateDocumentContent(sermon: SermonMeta, elements: SermonElement[]): Paragraph[] {
  const children: Paragraph[] = [];

  // ── Title block ──────────────────────────────────────────────────────────
  children.push(new Paragraph({
    children: [new TextRun({ text: sermon.title || 'Sermon', bold: true, size: 48, color: INK, font: 'Calibri' })],
    alignment: AlignmentType.CENTER,
    spacing: { after: 120 },
  }));

  if (sermon.scripture) {
    children.push(new Paragraph({
      children: [new TextRun({ text: sermon.scripture, italics: true, size: 26, color: DIM, font: 'Calibri' })],
      alignment: AlignmentType.CENTER,
      spacing: { after: 80 },
    }));
  }

  const dateStr = formatExportDate(sermon.date);
  if (dateStr) {
    children.push(new Paragraph({
      children: [new TextRun({ text: dateStr, size: 20, color: MUTED, font: 'Calibri' })],
      alignment: AlignmentType.CENTER,
      spacing: { after: 360 },
    }));
  }

  // ── Sections / content ─────────────────────────────────────────────────────
  elements.forEach(element => {
    if (element.type === 'section') {
      const hex = sectionHex(element.sectionType);
      const label = sectionLabel(element).toUpperCase();

      children.push(new Paragraph({
        children: [new TextRun({ text: label, bold: true, size: 22, color: hex, font: 'Calibri' })],
        spacing: { before: 360, after: 0 },
        shading: { type: ShadingType.SOLID, color: 'auto', fill: tint(hex, 0.9) },
        border: { bottom: { style: BorderStyle.SINGLE, color: hex, size: 8, space: 4 } },
      }));

      if (element.attribution) {
        children.push(new Paragraph({
          children: [new TextRun({ text: `— ${element.attribution}`, italics: true, size: 20, color: DIM, font: 'Calibri' })],
          alignment: AlignmentType.RIGHT,
          spacing: { before: 40, after: 120 },
        }));
      } else {
        children.push(new Paragraph({ children: [], spacing: { after: 80 } }));
      }

      htmlToBlocks(element.content).forEach(b => children.push(blockToParagraph(b)));
    } else {
      htmlToBlocks(element.content).forEach(b => children.push(blockToParagraph(b)));
    }
  });

  return children;
}

export async function createWordDocument(sermon: SermonMeta, sections: SermonElement[]): Promise<Blob> {
  const doc = new Document({
    styles: {
      default: {
        document: { run: { font: 'Calibri', size: BODY_SIZE, color: INK } },
      },
    },
    sections: [
      {
        properties: {
          page: { margin: { top: 1440, right: 1440, bottom: 1440, left: 1440 } },
        },
        children: generateDocumentContent(sermon, sections),
      },
    ],
  });

  return await Packer.toBlob(doc);
}
